import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { compressImage, validateImageFile, generateUniqueId, formatPrice } from '../../utils';

const BulkVendorImport = ({ onImportComplete }) => {
  const [importStep, setImportStep] = useState('upload'); // upload, preview, importing, complete
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef(null);
  const imageInputRefs = useRef({});

  const MAX_VENDORS = 100;
  const REQUIRED_FIELDS = ['businessName', 'category', 'city', 'contactEmail'];
  const ALLOWED_CATEGORIES = [
    'photography',
    'videography',
    'catering',
    'decoration',
    'music',
    'makeup',
    'mehndi',
    'venue',
    'planning',
    'other'
  ];

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { data: [], errors: ['CSV file is empty'] };

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const expectedHeaders = [
      'businessName',
      'category', 
      'city',
      'state',
      'contactEmail',
      'contactPhone',
      'description',
      'startingPrice',
      'website',
      'specialties'
    ];

    const missingHeaders = REQUIRED_FIELDS.filter(field => !headers.includes(field));
    if (missingHeaders.length > 0) {
      return { 
        data: [], 
        errors: [`Missing required columns: ${missingHeaders.join(', ')}`] 
      };
    }

    const data = [];
    const parseErrors = [];

    for (let i = 1; i < lines.length && data.length < MAX_VENDORS; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const row = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // Validation
      const rowErrors = [];
      
      if (!row.businessName) rowErrors.push('Business name is required');
      if (!row.category) rowErrors.push('Category is required');
      if (!ALLOWED_CATEGORIES.includes(row.category)) {
        rowErrors.push(`Invalid category. Must be one of: ${ALLOWED_CATEGORIES.join(', ')}`);
      }
      if (!row.city) rowErrors.push('City is required');
      if (!row.contactEmail || !/\S+@\S+\.\S+/.test(row.contactEmail)) {
        rowErrors.push('Valid email is required');
      }

      if (rowErrors.length > 0) {
        parseErrors.push(`Row ${i + 1}: ${rowErrors.join(', ')}`);
        continue;
      }

      // Add vendor with default values
      data.push({
        id: generateUniqueId(),
        name: row.businessName,
        category: row.category,
        city: row.city,
        state: row.state || '',
        location: `${row.city}${row.state ? ', ' + row.state : ''}`,
        email: row.contactEmail,
        phone: row.contactPhone || '',
        description: row.description || `Professional ${row.category} specializing in South Asian weddings.`,
        startingPrice: row.startingPrice ? parseInt(row.startingPrice) : 0,
        website: row.website || '',
        specialties: row.specialties ? row.specialties.split(';').map(s => s.trim()) : [],
        image: null,
        gallery: [],
        rating: 0,
        reviewCount: 0,
        isVerified: false,
        isFeatured: false,
        status: 'unclaimed',
        claimStatus: 'Claim Your Business',
        subscriptionType: 'free',
        joinDate: new Date().toISOString().split('T')[0],
        totalBookings: 0,
        responseTime: 'N/A',
        completionRate: 0,
        availability: {},
        businessHours: {},
        tags: ['imported', 'unclaimed'],
        coordinates: { lat: 0, lng: 0 }
      });
    }

    return { data, errors: parseErrors };
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setErrors(['Please upload a valid CSV file']);
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setErrors(['File size must be less than 5MB']);
      return;
    }

    setCsvFile(file);
    setErrors([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const { data, errors } = parseCSV(e.target.result);
      setParsedData(data);
      setPreviewData(data.slice(0, 10)); // Show first 10 for preview
      setErrors(errors);
      
      if (data.length > 0) {
        setImportStep('preview');
      }
    };
    reader.readAsText(file);
  };

  const handleImageUpload = async (vendorId, files) => {
    if (!files || files.length === 0) return;

    const vendor = parsedData.find(v => v.id === vendorId);
    if (!vendor) return;

    const processedImages = [];
    
    for (const file of Array.from(files)) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        setErrors(prev => [...prev, `${vendor.name}: ${validation.error}`]);
        continue;
      }

      try {
        // Compress and resize image
        const compressedFile = await compressImage(file, 800, 0.8);
        const imageUrl = URL.createObjectURL(compressedFile);
        
        processedImages.push({
          url: imageUrl,
          file: compressedFile,
          alt: `${vendor.name} - ${vendor.category}`,
          optimized: true
        });
      } catch (error) {
        setErrors(prev => [...prev, `${vendor.name}: Failed to process image ${file.name}`]);
      }
    }

    // Update vendor with images
    setParsedData(prev => prev.map(v => {
      if (v.id === vendorId) {
        return {
          ...v,
          image: processedImages[0]?.url || v.image,
          gallery: processedImages.map(img => img.url)
        };
      }
      return v;
    }));
  };

  const removeVendor = (vendorId) => {
    setParsedData(prev => prev.filter(v => v.id !== vendorId));
    setPreviewData(prev => prev.filter(v => v.id !== vendorId));
  };

  const startImport = async () => {
    if (parsedData.length === 0) return;

    setImportStep('importing');
    setIsProcessing(true);
    setImportProgress(0);

    // Simulate import process with progress
    const importPromises = parsedData.map((vendor, index) => {
      return new Promise((resolve) => {
        setTimeout(() => {
          setImportProgress(((index + 1) / parsedData.length) * 100);
          resolve(vendor);
        }, 100 * index);
      });
    });

    try {
      await Promise.all(importPromises);
      
      // In real implementation, this would save to Supabase
      // For now, we'll just store in localStorage or pass to parent
      const importedVendors = parsedData.map(vendor => ({
        ...vendor,
        importDate: new Date().toISOString(),
        importBatch: generateUniqueId()
      }));

      setImportStep('complete');
      onImportComplete?.(importedVendors);
      
    } catch (error) {
      setErrors(['Failed to import vendors. Please try again.']);
      setImportStep('preview');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetImport = () => {
    setImportStep('upload');
    setCsvFile(null);
    setParsedData([]);
    setPreviewData([]);
    setErrors([]);
    setImportProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent = [
      'businessName,category,city,state,contactEmail,contactPhone,description,startingPrice,website,specialties',
      'Rajesh Photography Studio,Wedding Photographer,San Francisco,CA,info@rajeshphoto.com,(415) 555-0123,"Professional wedding photographer with 8 years experience",2500,https://rajeshphoto.com,"Traditional;Modern;Candid"',
      'Mehndi by Priya,Mehndi Artist,New York,NY,priya@mehndi.com,(212) 555-0456,"Expert mehndi artist specializing in bridal designs",300,https://mehndibypriya.com,"Bridal;Traditional;Arabic"'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vendor_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="mb-6">
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Bulk Vendor Import
        </h3>
        <p className="text-text-secondary">
          Import up to {MAX_VENDORS} vendors at once using a CSV file. All imported vendors will be marked as unclaimed.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {['upload', 'preview', 'importing', 'complete'].map((step, index) => (
            <div key={step} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                importStep === step ? 'bg-primary text-white' :
                ['preview', 'importing', 'complete'].indexOf(importStep) > ['upload', 'preview', 'importing', 'complete'].indexOf(step) 
                  ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
              }`}>
                {step === 'complete' && ['preview', 'importing', 'complete'].indexOf(importStep) >= 3 ? (
                  <Icon name="Check" size={16} />
                ) : (
                  index + 1
                )}
              </div>
              {index < 3 && (
                <div className={`w-16 h-0.5 mx-2 ${
                  ['preview', 'importing', 'complete'].indexOf(importStep) > index ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-sm">
          <span>Upload CSV</span>
          <span>Preview Data</span>
          <span>Importing</span>
          <span>Complete</span>
        </div>
      </div>

      {/* Error Display */}
      {errors.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center mb-2">
            <Icon name="AlertTriangle" size={20} className="text-red-500 mr-2" />
            <h4 className="font-medium text-red-700">Import Errors</h4>
          </div>
          <ul className="text-sm text-red-600 space-y-1">
            {errors.slice(0, 10).map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
            {errors.length > 10 && (
              <li className="font-medium">... and {errors.length - 10} more errors</li>
            )}
          </ul>
        </div>
      )}

      {/* Upload Step */}
      {importStep === 'upload' && (
        <div className="space-y-6">
          <div className="border-2 border-dashed border-border-light rounded-lg p-8 text-center">
            <Icon name="Upload" size={48} className="mx-auto text-text-muted mb-4" />
            <h4 className="font-medium text-lg text-text-primary mb-2">
              Upload Vendor CSV File
            </h4>
            <p className="text-text-secondary mb-6">
              Select a CSV file with vendor information. Maximum {MAX_VENDORS} vendors per import.
            </p>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />
            
            <div className="space-y-3">
              <Button 
                variant="primary" 
                onClick={() => fileInputRef.current?.click()}
                iconName="Upload"
              >
                Choose CSV File
              </Button>
              
              <Button 
                variant="outline" 
                onClick={downloadTemplate}
                iconName="Download"
              >
                Download Template
              </Button>
            </div>
          </div>

          <div className="bg-surface-50 rounded-lg p-4">
            <h5 className="font-medium text-text-primary mb-3">CSV Format Requirements:</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h6 className="font-medium text-text-secondary mb-2">Required Fields:</h6>
                <ul className="space-y-1 text-text-muted">
                  <li>• businessName</li>
                  <li>• category</li>
                  <li>• city</li>
                  <li>• contactEmail</li>
                </ul>
              </div>
              <div>
                <h6 className="font-medium text-text-secondary mb-2">Optional Fields:</h6>
                <ul className="space-y-1 text-text-muted">
                  <li>• state</li>
                  <li>• contactPhone</li>
                  <li>• description</li>
                  <li>• startingPrice</li>
                  <li>• website</li>
                  <li>• specialties (semicolon separated)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Step */}
      {importStep === 'preview' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-lg text-text-primary">
                Preview Import Data
              </h4>
              <p className="text-text-secondary">
                {parsedData.length} vendors ready to import. You can add images and make adjustments.
              </p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={resetImport}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={startImport}
                disabled={parsedData.length === 0}
                iconName="Upload"
              >
                Import {parsedData.length} Vendors
              </Button>
            </div>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {parsedData.map((vendor) => (
              <div key={vendor.id} className="border border-border-light rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h5 className="font-medium text-text-primary">{vendor.name}</h5>
                    <p className="text-sm text-text-secondary">
                      {vendor.category} • {vendor.location}
                    </p>
                    <p className="text-sm text-text-muted mt-1">
                      {vendor.email} {vendor.phone && `• ${vendor.phone}`}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                      Unclaimed
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeVendor(vendor.id)}
                      iconName="X"
                    />
                  </div>
                </div>

                {vendor.description && (
                  <p className="text-sm text-text-secondary mb-3">{vendor.description}</p>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {vendor.startingPrice > 0 && (
                      <span className="text-sm font-medium text-primary">
                        Starting at {formatPrice(vendor.startingPrice)}
                      </span>
                    )}
                    {vendor.specialties?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {vendor.specialties.slice(0, 3).map((specialty, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 bg-surface-100 text-text-secondary text-xs rounded"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      ref={el => imageInputRefs.current[vendor.id] = el}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageUpload(vendor.id, e.target.files)}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => imageInputRefs.current[vendor.id]?.click()}
                      iconName="Image"
                    >
                      Add Images
                    </Button>
                  </div>
                </div>

                {vendor.gallery?.length > 0 && (
                  <div className="mt-3 flex space-x-2">
                    {vendor.gallery.slice(0, 4).map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`${vendor.name} image ${index + 1}`}
                        className="w-16 h-16 object-cover rounded border"
                      />
                    ))}
                    {vendor.gallery.length > 4 && (
                      <div className="w-16 h-16 bg-surface-100 rounded border flex items-center justify-center text-xs text-text-secondary">
                        +{vendor.gallery.length - 4}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Importing Step */}
      {importStep === 'importing' && (
        <div className="text-center py-12">
          <div className="mb-6">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h4 className="font-medium text-lg text-text-primary mb-2">
              Importing Vendors...
            </h4>
            <p className="text-text-secondary">
              Please wait while we process and import your vendor data.
            </p>
          </div>
          
          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-sm text-text-secondary mb-2">
              <span>Progress</span>
              <span>{Math.round(importProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${importProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Complete Step */}
      {importStep === 'complete' && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
          <h4 className="font-medium text-lg text-text-primary mb-2">
            Import Completed Successfully!
          </h4>
          <p className="text-text-secondary mb-6">
            {parsedData.length} vendors have been imported and marked as unclaimed. 
            They will appear in the vendor management section.
          </p>
          
          <div className="space-x-3">
            <Button variant="outline" onClick={resetImport}>
              Import More Vendors
            </Button>
            <Button variant="primary" onClick={() => window.location.reload()}>
              View Imported Vendors
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkVendorImport;